import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
    AlertDialog,
    Text,
    FormControl,
    Box,
    VStack,
    Button,
    Checkbox,
    Center,
    useTheme,
} from 'native-base';
import { StyleSheet } from 'react-native';
import Trans from '../../Components/Trans';
import DateTimePicker from '../../../Libs/DateTimePicker';
import GeneralAction from '../../../Actions/GeneralAction';
import TranslationAction from '../../../Actions/TranslationAction';
import APIAction from '../../../Actions/APIAction';
import RequestCacheAction from '../../../Actions/RequestCacheAction';
import { v4 as uuid } from 'uuid';
import main from '../../../Assets/Styles/main.json';

const mainStyle = StyleSheet.create(main);

const ProjectCopyModal = props => {
    const firstRender = useRef(true),
        headerFooterKey = useRef(uuid()),
        { colors } = useTheme(),
        [copyToDate, setCopyToDate] = useState(
            new Date().setDate(new Date().getDate() + 1),
        ),
        [isRepeat, setIsRepeat] = useState(false),
        [copyConfirm, setCopyConfirm] = useState(false),
        [copyWaiting, setCopyWaiting] = useState(false),
        [copyUntilDate, setCopyUntilDate] = useState(
            new Date().setMonth(new Date().getMonth() + 1),
        ),
        [daysOfWeek, setDaysOfWeek] = useState({
            1: '',
            2: '',
            3: '',
            4: '',
            5: '',
            6: '',
            7: '',
        }),
        [copyDays, setCopyDays] = useState([]),
        [copyDaysChecks, setCopyDaysChecks] = useState([]),
        [translations, setTranslations] = useState({
            Copy: '',
            Repeat: '',
        }),
        [copyEmployee, setCopyEmployee] = useState([]),
        [showWarningModal, setShowWarningModal] = useState(false),
        [warningText, setWarningText] = useState([]),
        [warningWaiting, setWarningWaiting] = useState(false)
        ;

    const onFirstLoad = useCallback(() => {
        const init = async () => {
            let newDaysOfWeek = {};
            let dateOfWeek = new Date();
            dateOfWeek.setDate(dateOfWeek.getDate() - dateOfWeek.getDay() + 1);

            for (let i = 1; i <= 7; i++) {
                let value = await GeneralAction.formatLocal(dateOfWeek, {
                    weekday: 'long',
                });
                newDaysOfWeek[i] = value;
                dateOfWeek.setDate(dateOfWeek.getDate() + 1);
            }

            setDaysOfWeek(newDaysOfWeek);

            //set translations
            let newTranslations = await TranslationAction.translateInLine(
                Object.keys(translations),
            );
            setTranslations(newTranslations);

            setCopyDaysChecks(
                Object.keys(newDaysOfWeek).map((day, index) => {
                    return (
                        <Checkbox value={day}>
                            <Text>{newDaysOfWeek[day]}</Text>
                        </Checkbox>
                    );
                }),
            );
        };
        if (translations['Copy'] === '' || translations['Repeat'] === '') {
            init();
        }
    }, [setDaysOfWeek, setTranslations, translations]);

    useEffect(() => {
        if (firstRender.current && props.isOpen) {
            firstRender.current = false;
            onFirstLoad();
        }
    }, [onFirstLoad, props.isOpen]);

    const copyProject = async () => {
        setCopyWaiting(true);
        let copyToDateString = new Date(copyToDate).toISOString();

        //create url
        let url = `/api/projects/${props.project.id}/copy-to-date/${copyToDateString}`;

        //create params
        let params = null;

        //is repeat
        if (isRepeat) {
            //check if days selected
            if (copyDays.length === 0) {
                setCopyWaiting(false);
                return GeneralAction.toast(
                    'danger',
                    <Trans>Please select at least one day</Trans>,
                );
            }
            // Can't copy for more than 6 months
            const sixMonthsFromCopyToDate = new Date(copyToDate);
            sixMonthsFromCopyToDate.setMonth(sixMonthsFromCopyToDate.getMonth() + 6);
            if (new Date(copyUntilDate) > sixMonthsFromCopyToDate) {
                setCopyWaiting(false);
                return GeneralAction.toast(
                    'danger',
                    <Trans>Can't copy for more than 6 months</Trans>,
                );
            }

            let copyUntilDateString = new Date(copyUntilDate).toISOString();
            params = {
                repeatUntil: copyUntilDateString,
                repeatDays: JSON.stringify(copyDays),
            };
        }

        //copy employees
        if (copyEmployee.length > 0) {
            if (params === null) {
                params = {};
            }
            params.copyEmployees = true;
        }

        //send request
        let response = await APIAction.request({
            method: 'GET',
            url: url,
            params: params,
        });

        let messages = [];
        for (let [date, employeeShift] of Object.entries(response)) {
            if (employeeShift.failedEmployeeShifts.length > 0) {
                let warningDate = await GeneralAction.formatLocal(new Date(date), { weekday: 'long', month: 'long', day: 'numeric' });
                messages.push(<Text underline>{warningDate}</Text>);
                for (let [key, shift] of Object.entries(employeeShift.failedEmployeeShifts)) {
                    let message = '';
                    message += shift.employee.user.firstName;
                    message += ' ';
                    message += shift.employee.user.lastName;
                    message += ' ';
                    message += await TranslationAction.translate('from');
                    message += ' ';
                    message += await GeneralAction.formatLocal(new Date(shift.shift.startOn), { hour: 'numeric', minute: 'numeric' });
                    message += ' ';
                    message += await TranslationAction.translate('to');
                    message += ' ';
                    message += await GeneralAction.formatLocal(new Date(shift.shift.endOn), { hour: 'numeric', minute: 'numeric' });
                    messages.push(<Text>{message}</Text>);
                };
            }
        }

        if (messages.length !== 0) {
            setWarningText(messages);
            setShowWarningModal(true);
        } else {
            await RequestCacheAction.clear({ url: 'projects' });
            if (typeof props.onReload === 'function') {
                props.onReload();
            }
        }
    };

    const closeWarningModal = async () => {
        setWarningWaiting(true);
        await RequestCacheAction.clear({ url: 'projects' });
        if (typeof props.onReload === 'function') {
            props.onReload();
        }
    };

    return (
        <>
            <AlertDialog
                isOpen={props.isOpen && !showWarningModal}
                onClose={() => {
                    setCopyConfirm(false);
                    setCopyToDate(new Date().setDate(new Date().getDate() + 1));
                    setIsRepeat(false);
                    setCopyDays([]);
                    props.onClose();
                }}>
                <AlertDialog.Content>
                    <AlertDialog.CloseButton />
                    <AlertDialog.Header key={headerFooterKey.current + '1'}>
                        {isRepeat ? (
                            <Text style={[mainStyle.modalTitle]}>{translations.Repeat}</Text>
                        ) : (
                            <Text style={[mainStyle.modalTitle]}>{translations.Copy}</Text>
                        )}
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <VStack space={2}>
                            {!copyConfirm && (
                                <Center>
                                    <Button.Group isAttached>
                                        <Button
                                            variant={isRepeat ? 'outline' : 'solid'}
                                            onPress={() => {
                                                setIsRepeat(false);
                                                headerFooterKey.current = uuid();
                                            }}>
                                            <Text color={isRepeat ? colors['primary']['600'] : '#fff'}>
                                                <Trans>Copy</Trans>
                                            </Text>
                                        </Button>
                                        <Button
                                            variant={isRepeat ? 'solid' : 'outline'}
                                            onPress={() => {
                                                setIsRepeat(true);
                                                headerFooterKey.current = uuid();
                                            }}>
                                            <Text color={isRepeat ? '#fff' : colors['primary']['600']}>
                                                <Trans>Repeat</Trans>
                                            </Text>
                                        </Button>
                                    </Button.Group>
                                </Center>
                            )}
                            {copyConfirm ? (
                                <Box>
                                    {isRepeat ? (
                                        <>
                                            <Text>
                                                <Trans>Repeat</Trans>{' '}
                                                <Text bold>
                                                    {props.project.name} <Trans>of</Trans>{' '}
                                                    {GeneralAction.formatDate(props.project.startDate)}
                                                </Text>
                                            </Text>
                                            <Text>
                                                <Trans>on the following days:</Trans>
                                            </Text>
                                            {copyDays.sort().map((day, index) => {
                                                return (
                                                    <Text>
                                                        - <Text bold>{daysOfWeek[day]}</Text>
                                                    </Text>
                                                );
                                            })}
                                            <Text>
                                                <Trans>Starting from</Trans>{' '}
                                                <Text bold>{GeneralAction.formatDate(copyToDate)}</Text>{' '}
                                                <Trans>and repeat this until</Trans>{' '}
                                                <Text bold>
                                                    {GeneralAction.formatDate(copyUntilDate)}
                                                </Text>
                                                .
                                            </Text>
                                        </>
                                    ) : (
                                        <Text>
                                            <Trans>Copy</Trans> {props.project.name} <Trans>from</Trans>{' '}
                                            {GeneralAction.formatDate(props.project.startDate)}{' '}
                                            <Trans>to</Trans> {GeneralAction.formatDate(copyToDate)}?
                                        </Text>
                                    )}
                                </Box>
                            ) : (
                                <Box>
                                    <VStack space={2}>
                                        {!isRepeat && (
                                            <>
                                                <Box>
                                                    <FormControl>
                                                        <FormControl.Label>
                                                            <Text>
                                                                <Trans>Copy to</Trans>
                                                            </Text>
                                                        </FormControl.Label>
                                                        <DateTimePicker
                                                            mode={'date'}
                                                            value={new Date(copyToDate)}
                                                            minimumDate={new Date().setDate(
                                                                new Date().getDate() + 1,
                                                            )}
                                                            onChange={date => {
                                                                setCopyToDate(new Date(date));
                                                            }}
                                                        />
                                                    </FormControl>
                                                </Box>
                                                <Box>
                                                    <Checkbox.Group onChange={values => setCopyEmployee(values)}>
                                                        <Checkbox value={true}>
                                                            <Text><Trans>Copy employees</Trans></Text>
                                                        </Checkbox>
                                                    </Checkbox.Group>
                                                </Box>
                                            </>
                                        )}

                                        {isRepeat && (
                                            <>
                                                <Box>
                                                    <FormControl>
                                                        <FormControl.Label>
                                                            <Text>
                                                                <Trans>For days</Trans>
                                                            </Text>
                                                        </FormControl.Label>
                                                        <Checkbox.Group
                                                            onChange={values => {
                                                                setCopyDays(values);
                                                            }}>
                                                            {copyDaysChecks}
                                                        </Checkbox.Group>
                                                    </FormControl>
                                                </Box>
                                                <Box>
                                                    <FormControl>
                                                        <FormControl.Label>
                                                            <Text>
                                                                <Trans>Repeat from</Trans>
                                                            </Text>
                                                        </FormControl.Label>
                                                        <DateTimePicker
                                                            mode={'date'}
                                                            value={new Date(copyToDate)}
                                                            minimumDate={new Date().setDate(
                                                                new Date().getDate() + 1,
                                                            )}
                                                            onChange={date => {
                                                                setCopyToDate(new Date(date));
                                                            }}
                                                        />
                                                    </FormControl>
                                                </Box>
                                                <Box>
                                                    <FormControl>
                                                        <FormControl.Label>
                                                            <Text>
                                                                <Trans>Repeat until</Trans>
                                                            </Text>
                                                        </FormControl.Label>
                                                        <DateTimePicker
                                                            mode={'date'}
                                                            value={new Date(copyUntilDate)}
                                                            minimumDate={new Date(copyToDate)}
                                                            maximumDate={new Date(
                                                                new Date(copyToDate).setMonth(
                                                                    new Date(copyToDate).getMonth() + 6,
                                                                ),
                                                            ).setDate(new Date(copyToDate).getDate() - 1)}
                                                            onChange={date => {
                                                                setCopyUntilDate(new Date(date));
                                                            }}
                                                        />
                                                    </FormControl>
                                                </Box>
                                                <Box>
                                                    <Checkbox.Group onChange={values => setCopyEmployee(values)}>
                                                        <Checkbox value={true}>
                                                            <Text><Trans>Copy employees</Trans></Text>
                                                        </Checkbox>
                                                    </Checkbox.Group>
                                                </Box>
                                            </>
                                        )}
                                    </VStack>
                                </Box>
                            )}
                        </VStack>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <Button.Group space={2} key={headerFooterKey.current + '2'}>
                            <Button
                                variant={'ghost'}
                                onPress={() => {
                                    setCopyConfirm(false);
                                    setCopyToDate(new Date().setDate(new Date().getDate() + 1));
                                    setIsRepeat(false);
                                    setCopyDays([]);
                                    props.onClose();
                                }}>
                                <Text>
                                    <Trans>Cancel</Trans>
                                </Text>
                            </Button>
                            {copyConfirm ? (
                                <Button
                                    colorScheme={'primary'}
                                    onPress={() => {
                                        copyProject();
                                    }}
                                    isDisabled={copyWaiting}
                                    isLoading={copyWaiting}>
                                    <Text color={'#fff'}>
                                        <Trans>Confirm</Trans>
                                    </Text>
                                </Button>
                            ) : (
                                <Button
                                    isDisabled={isRepeat && copyDays.length === 0}
                                    colorScheme={'primary'}
                                    onPress={() => {
                                        setCopyConfirm(true);
                                    }}>
                                    {isRepeat ? (
                                        <Text color={'#fff'}>{translations.Repeat}</Text>
                                    ) : (
                                        <Text color={'#fff'}>{translations.Copy}</Text>
                                    )}
                                </Button>
                            )}
                        </Button.Group>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog>
            <AlertDialog isOpen={showWarningModal} onClose={() => closeWarningModal()} >
                <AlertDialog.Content>
                    <AlertDialog.CloseButton />
                    <AlertDialog.Header><Text style={[mainStyle.modalTitle]}><Trans>Warning</Trans></Text></AlertDialog.Header>
                    <AlertDialog.Body>
                        <VStack space={2}>
                            <Text bold><Trans>Could not plan the following employees:</Trans></Text>
                            {warningText}
                        </VStack>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <Button.Group space={2}>
                            <Button
                                colorScheme={'primary'}
                                onPress={() => closeWarningModal()}
                                isDisabled={warningWaiting}
                                isLoading={warningWaiting}
                            >
                                <Text color={'#fff'}><Trans>Close</Trans></Text>
                            </Button>
                        </Button.Group>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog>
        </>
    );
};

export default ProjectCopyModal;